import { useEffect, useRef, useState } from 'react'
import inspectorApi from '../services/inspector/api'
import type { ExceptionDrawerDetailBlock } from '../config/exceptions/types'
import type { ReferenceLink } from '../components/shared/referenceLinks/types'

interface UseExceptionDetailBlocksResult {
  detailBlocks: ExceptionDrawerDetailBlock[] | null
  links: ReferenceLink[]
  loading: boolean
  error: string | null
}

/**
 * Lazy "Why + How" detail for one exception tag, keyed by ``tagId``.
 *
 * Feeds the drawer's linked-sibling expand (phase 3b) and the Admin drawer
 * from the single read-only ``/exception-tags/{tagId}/detail/{fpCode}``
 * endpoint. Only fetches while ``enabled`` (the sibling is expanded / the
 * admin drawer is open), and a generation ref drops stale responses if the
 * tag changes mid-flight.
 */
export function useExceptionDetailBlocks(
  fpCode: string | undefined,
  tagId: number | undefined,
  enabled: boolean = true,
): UseExceptionDetailBlocksResult {
  const [state, setState] = useState<UseExceptionDetailBlocksResult>({
    detailBlocks: null,
    links: [],
    loading: false,
    error: null,
  })
  const genRef = useRef(0)

  useEffect(() => {
    if (!enabled || !fpCode || tagId == null) return
    const gen = ++genRef.current
    setState((s) => ({ ...s, loading: true, error: null }))
    inspectorApi
      .getExceptionTagDetail(fpCode, tagId)
      .then((res) => {
        if (gen !== genRef.current) return
        setState({
          detailBlocks: res.detail_blocks ?? [],
          links: res.links ?? [],
          loading: false,
          error: null,
        })
      })
      .catch((e: unknown) => {
        if (gen !== genRef.current) return
        const message =
          e instanceof Error ? e.message : 'Failed to load exception detail'
        setState({ detailBlocks: null, links: [], loading: false, error: message })
      })
  }, [fpCode, tagId, enabled])

  return state
}

export default useExceptionDetailBlocks
